import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow,
  Download,
  School,
  Group,
  Security,
  VideoLibrary,
  Description,
  CheckCircle,
  PersonAdd,
  Upload,
  CloudSync,
  Web,
  AutoAwesome,
  Shield,
  Speed,
  TrendingUp,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import Navbar from '../../components/Navbar/Navbar';
import AdminNavbar from '../../components/Navbar/AdminNavbar';
import Footer from '../../components/Footer/Footer';
import { onAuthStateChanged } from 'firebase/auth';
import { authen } from '../../firebase/firebase';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { environment } from '../../../environments/environment';

// Advanced animations
const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(60px) scale(0.9); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

const slideInLeft = keyframes`
  from { 
    opacity: 0; 
    transform: translateX(-80px) rotateY(-10deg); 
  }
  to { 
    opacity: 1; 
    transform: translateX(0) rotateY(0deg); 
  }
`;

const slideInRight = keyframes`
  from { 
    opacity: 0; 
    transform: translateX(80px) rotateY(10deg); 
  }
  to { 
    opacity: 1; 
    transform: translateX(0) rotateY(0deg); 
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-15px) rotate(2deg); }
  50% { transform: translateY(0px) rotate(0deg); }
  75% { transform: translateY(-8px) rotate(-1deg); }
`;

const glow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 30px rgba(42, 146, 53, 0.2);
  }
  50% { 
    box-shadow: 0 0 50px rgba(42, 146, 53, 0.4);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const checkAnimation = keyframes`
  0% { opacity: 0; transform: translateX(-10px) scale(0.8); }
  50% { transform: scale(1.1); }
  100% { opacity: 1; transform: translateX(0) scale(1); }
`;

const comingSoonPulse = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const comingSoonFloat = keyframes`
  0%, 100% { 
    transform: translateY(0px) rotate(0deg);
  }
  25% { 
    transform: translateY(-10px) rotate(2deg);
  }
  50% { 
    transform: translateY(0px) rotate(0deg);
  }
  75% { 
    transform: translateY(-5px) rotate(-1deg);
  }
`;

const comingSoonGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(42, 146, 53, 0.3);
  }
  50% { 
    box-shadow: 0 0 40px rgba(42, 146, 53, 0.6);
  }
`;

const textReveal = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(20px) scale(0.9);
  }
  50% { 
    opacity: 0.8; 
    transform: translateY(-5px) scale(1.02);
  }
  100% { 
    opacity: 1; 
    transform: translateY(0) scale(1);
  }
`;

// Styled Components
const MainContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `
    radial-gradient(circle at 15% 15%, rgba(42, 146, 53, 0.12) 0%, transparent 60%),
    radial-gradient(circle at 85% 85%, rgba(76, 175, 80, 0.08) 0%, transparent 60%),
    radial-gradient(circle at 50% 20%, rgba(139, 195, 74, 0.06) 0%, transparent 70%),
    radial-gradient(circle at 20% 80%, rgba(102, 187, 106, 0.05) 0%, transparent 60%),
    linear-gradient(135deg, #fafafa 0%, #f0f4f8 100%)
  `,
  position: 'relative',
  overflow: 'hidden',
  paddingTop: '100px',
  paddingBottom: theme.spacing(8),
  width: '100%',
}));

const FloatingElement = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  position: 'absolute',
  opacity: 0.04,
  animation: `${float} 12s ease-in-out ${delay}s infinite`,
  pointerEvents: 'none',
  color: '#2A9235',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(10),
  position: 'relative',
  zIndex: 2,
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.5rem, 3.5vw, 2.2rem)',
  fontWeight: 600,
  color: '#000000', // Black as requested
  textAlign: 'center',
  marginBottom: theme.spacing(2), // more breathing room under the main title
  letterSpacing: '-0.01em',
  lineHeight: 1.2,
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
  fontWeight: 400,
  color: '#6b7280',
  textAlign: 'center',
  lineHeight: 1.6,
  maxWidth: '560px',
  margin: '0 auto',
  marginTop: theme.spacing(0.5), // spacing from the main title
  opacity: 1,
}));

const SectionContainer = styled(Card)(({ theme }) => ({
  borderRadius: 28,
  background: 'rgba(255, 255, 255, 0.95)', // Slightly more opaque
  backdropFilter: 'blur(25px)',
  border: '2px solid rgba(42, 146, 53, 0.15)', // Slightly more visible border
  boxShadow: '0 30px 100px rgba(42, 146, 53, 0.12), 0 15px 40px rgba(0, 0, 0, 0.1)', // Enhanced shadows
  marginBottom: theme.spacing(4),
  overflow: 'hidden',
  position: 'relative',
  minHeight: '600px',
  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  width: '100%',
  maxWidth: '100%',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 40px 120px rgba(42, 146, 53, 0.18), 0 20px 50px rgba(0, 0, 0, 0.12)', // Even stronger hover shadows
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 30%, #66BB6A 60%, #81C784 100%)',
    borderRadius: '28px 28px 0 0',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 3, 2.5, 3),
  borderBottom: '1px solid rgba(42, 146, 53, 0.1)',
  // Made slightly greenish background
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.08) 0%, rgba(76, 175, 80, 0.06) 50%, rgba(255, 255, 255, 0.95) 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  minHeight: 140,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '50px',
    height: '2px',
    background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 100%)',
    borderRadius: '2px',
  },
}));

const SectionTitle = styled(Typography)({
  fontSize: 'clamp(1.4rem, 2.8vw, 1.9rem)',
  fontWeight: 800,
  color: '#1a1a1a',
  marginBottom: 12,
  position: 'relative',
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
});

const SectionDescription = styled(Typography)(({ theme }) => ({
  color: '#64748b',
  fontSize: 'clamp(0.9rem, 1.6vw, 1rem)',
  lineHeight: 1.6,
  maxWidth: '90%',
  margin: '0 auto',
  fontWeight: 400,
}));

const ResourceCard = styled(Card)<{ delay?: number }>(({ theme, delay = 0 }) => ({
  height: '100%',
  borderRadius: 20,
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.95) 100%)',
  border: '2px solid rgba(42, 146, 53, 0.12)', // More visible border
  // Enhanced shadows for better visibility
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 10px 25px rgba(42, 146, 53, 0.15), 0 5px 15px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  minHeight: '350px',
  maxHeight: '450px',
  display: 'flex',
  flexDirection: 'column',
  animation: `${fadeInUp} 1s ease-out ${delay}s both`,
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)', // Slightly more pronounced hover
    // Much stronger hover shadows
    boxShadow: '0 30px 60px rgba(42, 146, 53, 0.2), 0 20px 40px rgba(0, 0, 0, 0.15), 0 10px 25px rgba(42, 146, 53, 0.1)',
    '& .card-icon': {
      transform: 'scale(1.1) rotate(8deg)',
      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
      boxShadow: '0 12px 30px rgba(42, 146, 53, 0.4)',
      '& .MuiSvgIcon-root': {
        color: '#fff',
      },
    },
    '& .shimmer-effect': {
      left: '100%',
    },
    '& .feature-item': {
      '& .check-icon': {
        transform: 'scale(1.1)',
        color: '#2A9235',
      },
      '& .feature-text': {
        color: '#1a1a1a',
        transform: 'translateX(3px)',
      },
    },
  },
  '& .shimmer-effect': {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(42, 146, 53, 0.12), transparent)',
    transition: 'left 0.7s ease',
    pointerEvents: 'none',
  },
}));

const IconWrapper = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  background: 'linear-gradient(135deg, #f8fafc 0%, #e1e8ed 100%)',
  border: '3px solid rgba(42, 146, 53, 0.15)',
  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  marginBottom: theme.spacing(1.5),
  flexShrink: 0,
  boxShadow: '0 8px 20px rgba(42, 146, 53, 0.12)', // Enhanced icon shadow
  '& .MuiSvgIcon-root': {
    fontSize: 24,
    color: '#2A9235',
    transition: 'all 0.6s ease',
  },
}));

const ActionButton = styled(Button)<{ buttonVariant: 'primary' | 'secondary' }>(({ buttonVariant }) => ({
  borderRadius: 14,
  padding: '10px 16px',
  fontSize: '0.85rem',
  fontWeight: 700,
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  minWidth: 100,
  ...(buttonVariant === 'primary' ? {
    background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
    color: '#fff',
    boxShadow: '0 8px 25px rgba(42, 146, 53, 0.35)', // Enhanced button shadows
    '&:hover': {
      transform: 'translateY(-2px) scale(1.01)',
      boxShadow: '0 15px 35px rgba(42, 146, 53, 0.45)',
      background: 'linear-gradient(135deg, #1e7e29 0%, #388e3c 100%)',
    },
  } : {
    background: 'rgba(255, 255, 255, 0.95)',
    color: '#2A9235',
    border: '2px solid #2A9235',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 6px 20px rgba(42, 146, 53, 0.15)', // Added shadow for secondary button
    '&:hover': {
      background: 'rgba(42, 146, 53, 0.08)',
      transform: 'translateY(-2px) scale(1.01)',
      boxShadow: '0 12px 30px rgba(42, 146, 53, 0.25)',
      borderColor: '#1e7e29',
    },
  }),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
    transition: 'left 0.7s',
  },
  '&:hover::before': {
    left: '100%',
  },
}));

const FeatureItem = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 0',
  borderRadius: 10,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${checkAnimation} 0.6s ease-out ${delay}s both`,
  '&:hover': {
    background: 'rgba(42, 146, 53, 0.04)',
    padding: '6px 10px',
    margin: '0 -10px',
  },
  '& .check-icon': {
    fontSize: 16,
    color: '#2A9235',
    transition: 'all 0.3s ease',
    filter: 'drop-shadow(0 2px 4px rgba(42, 146, 53, 0.2))',
  },
  '& .feature-text': {
    color: '#374151',
    fontWeight: 600,
    fontSize: '0.85rem',
    transition: 'all 0.3s ease',
    lineHeight: 1.4,
  },
}));

const ComingSoonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  padding: theme.spacing(6),
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.05) 0%, rgba(76, 175, 80, 0.03) 100%)',
  borderRadius: '20px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(42, 146, 53, 0.1) 0%, transparent 70%)',
    animation: `${comingSoonPulse} 3s ease-in-out infinite`,
  },
}));

const ComingSoonIcon = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 50%, #66BB6A 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  position: 'relative',
  animation: `${comingSoonFloat} 4s ease-in-out infinite, ${comingSoonGlow} 2s ease-in-out infinite`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -4,
    left: -4,
    right: -4,
    bottom: -4,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.3) 0%, rgba(76, 175, 80, 0.2) 100%)',
    zIndex: -1,
    animation: `${comingSoonPulse} 2s ease-in-out infinite`,
  },
}));

const ComingSoonTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(2rem, 4vw, 3rem)',
  fontWeight: 800,
  color: '#1a1a1a',
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: `${textReveal} 1s ease-out 0.5s both`,
  letterSpacing: '-0.02em',
  lineHeight: 1.2,
}));

const ComingSoonSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
  color: '#64748b',
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  animation: `${textReveal} 1s ease-out 0.8s both`,
  lineHeight: 1.6,
  maxWidth: '500px',
}));

const ComingSoonChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.15) 0%, rgba(76, 175, 80, 0.1) 100%)',
  color: '#2A9235',
  fontSize: '1.1rem',
  fontWeight: 700,
  padding: theme.spacing(2, 3),
  borderRadius: '25px',
  border: '2px solid rgba(42, 146, 53, 0.2)',
  animation: `${textReveal} 1s ease-out 1.1s both, ${comingSoonPulse} 3s ease-in-out infinite 1.5s`,
  '& .MuiChip-label': {
    padding: theme.spacing(1, 2),
  },
}));

const Resources: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const db = getFirestore();
  
  const [videoOpen, setVideoOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState<any>(null);

  const sampleVideoUrl = 'coming-soon';

  useEffect(() => {
    const unsub = onAuthStateChanged(authen, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setIsAdmin(false);
        return;
      }

      setUser(currentUser);

      try {
        let caData: any | null = null;
        const caRef = doc(db, 'custom_auth', currentUser.uid);
        const caSnap = await getDoc(caRef);
        if (caSnap.exists()) {
          caData = caSnap.data();
        } else if (currentUser.email) {
          const q = query(
            collection(db, 'custom_auth'),
            where('email', '==', currentUser.email)
          );
          const qs = await getDocs(q);
          if (!qs.empty) caData = qs.docs[0].data();
        }

        const userRole = caData?.role || caData?.admin_role || caData?.user_type || 'user';
        setIsAdmin(userRole === 'admin' || userRole === 'superadmin');
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    });

    return () => unsub();
  }, [db]);

  const resourcesData = {
    usernameSignup: {
      title: 'Username Signup',
      description: 'Manually create or import usernames for your users',
      options: [
        {
          id: 'generate-usernames',
          title: 'Generate New Usernames',
          description: 'Let our system automatically create secure usernames for your students.',
          icon: PersonAdd,
          features: ['Auto-generation', 'Secure defaults', 'Bulk creation', 'Easy management'],
          videoTitle: 'Generate Usernames walkthrough',
          pdfTitle: 'Username Generation Guide',
          videoUrl: sampleVideoUrl,
          pdfUrl: environment.resources.generateUsernamesPdfUrl,
        },
        {
          id: 'bring-usernames',
          title: 'Bring Your Own Usernames',
          description: 'Import existing student registration numbers for your students.',
          icon: Upload,
          features: ['Import existing', 'Custom creation', 'CSV upload', 'Full control'],
          videoTitle: 'Import Usernames walkthrough',
          pdfTitle: 'Username Import Guide',
          videoUrl: sampleVideoUrl,
          pdfUrl: environment.resources.importUsernamesPdfUrl,
        },
      ],
    },
    ssoIntegration: {
      title: 'SSO Integration',
      description: 'Connect with your school\'s existing identity provider for seamless access',
      options: [
        {
          id: 'clever-integration',
          title: 'Clever Integration',
          description: 'Connect directly through Clever for automatic roster synchronization.',
          icon: CloudSync,
          features: ['Direct integration', 'Auto sync', 'Real-time updates', 'Clever dashboard'],
          videoTitle: 'Clever Setup walkthrough',
          pdfTitle: 'Clever Integration Guide',
          videoUrl: environment.resources.cleverVideoUrl,
          pdfUrl: environment.resources.cleverPdfUrl,
        },
        {
          id: 'classlink-integration',
          title: 'ClassLink Integration',
          description: 'Set up ClassLink integration for streamlined access and management.',
          icon: Web,
          features: ['ClassLink portal', 'Single sign-on', 'Roster management', 'Easy deployment'],
          videoTitle: 'ClassLink Setup walkthrough',
          pdfTitle: 'ClassLink Integration Guide',
          videoUrl: environment.resources.classlinkVideoUrl,
          pdfUrl: environment.resources.classlinkPdfUrl,
        },
      ],
    },
  };

  const handleVideoOpen = (resource: any) => {
    setSelectedResource(resource);
    setVideoOpen(true);
  };

  const handlePdfOpen = (resource: any) => {
    setSelectedResource(resource);
    setPdfOpen(true);
  };

  return (
    <>
      {isAdmin ? <AdminNavbar /> : <Navbar currentPage="resources" />}
      <MainContainer>
        {/* Floating Background Elements */}
        <FloatingElement sx={{ top: '8%', left: '3%' }} delay={0}>
          <School sx={{ fontSize: 140 }} />
        </FloatingElement>
        <FloatingElement sx={{ top: '15%', right: '5%' }} delay={3}>
          <VideoLibrary sx={{ fontSize: 120 }} />
        </FloatingElement>
        <FloatingElement sx={{ bottom: '20%', left: '8%' }} delay={6}>
          <Security sx={{ fontSize: 130 }} />
        </FloatingElement>
        <FloatingElement sx={{ bottom: '10%', right: '10%' }} delay={9}>
          <AutoAwesome sx={{ fontSize: 110 }} />
        </FloatingElement>

        <Container 
          maxWidth={false} // Allow full width
          sx={{ 
            px: { xs: 2, sm: 3, md: 4, lg: 6 }, // Increased horizontal padding for wider containers
            width: '100%',
            maxWidth: '1600px', // Increased from 1400px to make containers wider
            margin: '0 auto',
          }}
        >
          {/* Main Hero Section */}
          <HeroSection>
            <MainTitle variant="h1">
              Onboarding & Roster Setup Resources
            </MainTitle>
            <SubTitle variant="h5">
              Choose the best way to get your students set up. You can create 
              usernames directly or integrate with your school's SSO provider.
            </SubTitle>
          </HeroSection>

          {/* Main Content Container - Made even wider */}
          <Grid 
            container 
            spacing={{ xs: 3, md: 4, lg: 5 }} // Increased spacing
            sx={{ 
              width: '100%',
              margin: 0,
              '& .MuiGrid-item': {
                paddingLeft: { xs: 1.5, md: 2, lg: 2.5 },
                paddingTop: { xs: 1.5, md: 2, lg: 2.5 },
              }
            }}
          >
            {/* Username Signup Section */}
            <Grid item xs={12} lg={6}>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle variant="h3">
                    {resourcesData.usernameSignup.title}
                  </SectionTitle>
                  <SectionDescription variant="body1">
                    {resourcesData.usernameSignup.description}
                  </SectionDescription>
                </SectionHeader>
                
                <CardContent sx={{ p: { xs: 2.5, md: 3.5, lg: 4 }, height: '100%' }}> {/* Increased padding */}
                  <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="stretch" sx={{ height: '100%' }}>
                    {resourcesData.usernameSignup.options.map((option, index) => {
                      const IconComponent = option.icon;
                      return (
                        <Grid item xs={12} md={6} key={option.id} sx={{ display: 'flex' }}>
                          <ResourceCard delay={index * 0.2} sx={{ width: '100%' }} className="resource-card">
                            <div className="shimmer-effect" />
                            <CardContent sx={{ 
                              p: { xs: 2.5, md: 3 }, // Increased padding
                              height: '100%', 
                              display: 'flex', 
                              flexDirection: 'column'
                            }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.5 }}>
                                <IconWrapper className="card-icon">
                                  <IconComponent />
                                </IconWrapper>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 700, 
                                  color: '#1a1a1a',
                                  fontSize: 'clamp(0.95rem, 1.6vw, 1.05rem)',
                                  mb: 1,
                                  lineHeight: 1.3,
                                  textAlign: 'center'
                                }}>
                                  {option.title}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: '#64748b', 
                                  lineHeight: 1.5,
                                  fontSize: '0.85rem',
                                  textAlign: 'center',
                                  mb: 2
                                }}>
                                  {option.description}
                                </Typography>
                              </Box>

                              <Box sx={{ mb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Stack spacing={1} alignItems="stretch">
                                  {option.features.map((feature, idx) => (
                                    <FeatureItem key={idx} delay={0.1 * idx} className="feature-item">
                                      <CheckCircle className="check-icon" />
                                      <Typography variant="body2" className="feature-text">
                                        {feature}
                                      </Typography>
                                    </FeatureItem>
                                  ))}
                                </Stack>
                              </Box>

                              <Box sx={{ mt: 'auto', pt: 1.5 }}>
                                <Stack direction="row" spacing={1}>
                                  <ActionButton
                                    buttonVariant="primary"
                                    size="small"
                                    startIcon={<PlayArrow />}
                                    onClick={() => handleVideoOpen(option)}
                                    sx={{ flex: 1 }}
                                  >
                                    Watch
                                  </ActionButton>
                                  <ActionButton
                                    buttonVariant="secondary"
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => handlePdfOpen(option)}
                                    sx={{ flex: 1 }}
                                  >
                                    Guide
                                  </ActionButton>
                                </Stack>
                              </Box>
                            </CardContent>
                          </ResourceCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </SectionContainer>
            </Grid>

            {/* SSO Integration Section */}
            <Grid item xs={12} lg={6}>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle variant="h3">
                    {resourcesData.ssoIntegration.title}
                  </SectionTitle>
                  <SectionDescription variant="body1">
                    {resourcesData.ssoIntegration.description}
                  </SectionDescription>
                </SectionHeader>
                
                <CardContent sx={{ p: { xs: 2.5, md: 3.5, lg: 4 }, height: '100%' }}> {/* Increased padding */}
                  <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="stretch" sx={{ height: '100%' }}>
                    {resourcesData.ssoIntegration.options.map((option, index) => {
                      const IconComponent = option.icon;
                      return (
                        <Grid item xs={12} md={6} key={option.id} sx={{ display: 'flex' }}>
                          <ResourceCard delay={0.4 + index * 0.2} sx={{ width: '100%' }} className="resource-card">
                            <div className="shimmer-effect" />
                            <CardContent sx={{ 
                              p: { xs: 2.5, md: 3 }, // Increased padding
                              height: '100%', 
                              display: 'flex', 
                              flexDirection: 'column'
                            }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.5 }}>
                                <IconWrapper className="card-icon">
                                  <IconComponent />
                                </IconWrapper>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 700, 
                                  color: '#1a1a1a',
                                  fontSize: 'clamp(0.95rem, 1.6vw, 1.05rem)',
                                  mb: 1,
                                  lineHeight: 1.3,
                                  textAlign: 'center'
                                }}>
                                  {option.title}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: '#64748b', 
                                  lineHeight: 1.5,
                                  fontSize: '0.85rem',
                                  textAlign: 'center',
                                  mb: 2
                                }}>
                                  {option.description}
                                </Typography>
                              </Box>

                              <Box sx={{ mb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Stack spacing={1} alignItems="stretch">
                                  {option.features.map((feature, idx) => (
                                    <FeatureItem key={idx} delay={0.1 * idx} className="feature-item">
                                      <CheckCircle className="check-icon" />
                                      <Typography variant="body2" className="feature-text">
                                        {feature}
                                      </Typography>
                                    </FeatureItem>
                                  ))}
                                </Stack>
                              </Box>

                              <Box sx={{ mt: 'auto', pt: 1.5 }}>
                                <Stack direction="row" spacing={1}>
                                  <ActionButton
                                    buttonVariant="primary"
                                    size="small"
                                    startIcon={<PlayArrow />}
                                    onClick={() => handleVideoOpen(option)}
                                    sx={{ flex: 1 }}
                                  >
                                    Watch
                                  </ActionButton>
                                  <ActionButton
                                    buttonVariant="secondary"
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={() => handlePdfOpen(option)}
                                    sx={{ flex: 1 }}
                                  >
                                    Guide
                                  </ActionButton>
                                </Stack>
                              </Box>
                            </CardContent>
                          </ResourceCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </SectionContainer>
            </Grid>
          </Grid>
        </Container>

        {/* Video Dialog */}
        <Dialog
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '28px',
              overflow: 'hidden',
              maxHeight: '85vh',
            },
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.10) 0%, rgba(76, 175, 80, 0.10) 100%)',
            color: '#1a1a1a',
            borderBottom: '1px solid rgba(42, 146, 53, 0.15)'
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedResource?.videoTitle}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Step-by-step walkthrough
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setVideoOpen(false)}
              sx={{ 
                color: '#1a1a1a', 
                '&:hover': { 
                  background: 'rgba(42,146,53,0.08)',
                  transform: 'scale(1.1)'
                } 
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            {selectedResource?.videoUrl && selectedResource.videoUrl !== 'coming-soon' ? (
              <Box sx={{ width: '100%', height: { xs: '50vh', md: '65vh' }, position: 'relative' }}>
                <video
                  controls
                  autoPlay
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  src={selectedResource.videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </Box>
            ) : (
              <ComingSoonContainer>
                <ComingSoonIcon>
                  <VideoLibrary sx={{ fontSize: 60, color: '#fff' }} />
                </ComingSoonIcon>
                <ComingSoonTitle>
                  Coming Soon
                </ComingSoonTitle>
              </ComingSoonContainer>
            )}
          </DialogContent>
        </Dialog>
        
        {/* PDF Dialog */}
        <Dialog
          open={pdfOpen}
          onClose={() => setPdfOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '28px',
              overflow: 'hidden',
              maxHeight: '85vh',
            },
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.10) 0%, rgba(76, 175, 80, 0.10) 100%)',
            color: '#1a1a1a',
            borderBottom: '1px solid rgba(42, 146, 53, 0.15)'
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedResource?.pdfTitle}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Comprehensive setup guide
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setPdfOpen(false)}
              sx={{ 
                color: '#1a1a1a', 
                '&:hover': { 
                  background: 'rgba(42,146,53,0.08)',
                  transform: 'scale(1.1)'
                } 
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            {selectedResource?.pdfUrl ? (
              <Box sx={{ width: '100%', height: { xs: '50vh', md: '65vh' } }}>
                <iframe
                  src={selectedResource.pdfUrl}
                  title={selectedResource.pdfTitle}
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              </Box>
            ) : (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <Description sx={{ fontSize: 80, color: 'rgba(42, 146, 53, 0.6)', mb: 3 }} />
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                  Guide Coming Soon
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
                  We're preparing comprehensive documentation for you.
                </Typography>
                <Chip 
                  label="📚 In Production" 
                  sx={{ 
                    background: 'rgba(42, 146, 53, 0.10)',
                    color: '#1a1a1a',
                    fontSize: '1rem',
                    padding: '24px 16px'
                  }} 
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </MainContainer>
      <Footer/>
    </>
  );
};

export default Resources;
